import { useState, useEffect, useRef, lazy, Suspense } from "react";
import "./styles/global.css";
import { LANG, SECTION_IDS } from "./data/translations";
import { TRACKS, GALLERY_ITEMS, SOCIALS, STATS, LINKS } from "./data/content";
import { MagBtn, TiltCard, Counter } from "./components/ui";

const WireSphere = lazy(() => import("./components/canvas").then(m => ({ default: m.WireSphere })));
const CircleViz = lazy(() => import("./components/canvas").then(m => ({ default: m.CircleViz })));
const TODAY = new Date().toISOString().split('T')[0];

export default function App() {
  const [lang, setLang] = useState('ru');
  const navLinksRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [paused, setPaused] = useState(false);
  const audioRef = useRef(null);
  const fpProgRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const navRef = useRef(null);
  const scrollProgRef = useRef(null);
  const bttRef = useRef(null);
  const tgRef = useRef(null);
  const [vis, setVis] = useState(new Set);
  const visRef = useRef(new Set);
  const [curtain, setCurtain] = useState(true);
  const [curtainOpen, setCurtainOpen] = useState(false);
  const heroRef = useRef(null);
  const cursorRef = useRef(null);
  const heroContentRef = useRef(null);
  const heroGhostRef = useRef(null);
  // cursorHover via ref — no re-renders
  const [videoModal, setVideoModal] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const galProgRef = useRef(null);
  const [cookieOk, setCookieOk] = useState(() => localStorage.getItem('cookie-consent') === '1');
  const LANGS = ['ru', 'en', 'ar'];
  const nextLang = () => LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length];
  const t = LANG[lang];
  const li = lang === 'ru' ? 0 : 1;
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  useEffect(() => {
    const el = heroRef.current;
    const add = (cls, ms) => setTimeout(() => el?.classList.add(cls), ms);
    const t0 = setTimeout(() => setCurtainOpen(true), 400);
    const t1 = setTimeout(() => setCurtain(false), 1200);
    const ids = [t0, t1, add('hp2', 1400), add('hp3', 2000), add('hp4', 2600), add('hp5', 3200), add('hp6', 3800)];
    return () => ids.forEach(id => clearTimeout(id));
  }, []);

  const scrollRaf = useRef(null);
  useEffect(() => {
    const h = () => {
      if (scrollRaf.current) return;
      scrollRaf.current = requestAnimationFrame(() => {
        const sy = window.scrollY;
        const d = document.documentElement;
        const pct = d.scrollTop / (d.scrollHeight - d.clientHeight);
        if (navRef.current) {
          navRef.current.style.background = `rgba(3,3,3,${Math.min(sy / 150, .9)})`;
          navRef.current.style.backdropFilter = sy > 80 ? 'blur(24px)' : 'none';
          navRef.current.style.borderBottom = `1px solid rgba(196,148,74,${Math.min(sy / 300, .06)})`;
        }
        if (scrollProgRef.current) scrollProgRef.current.style.width = `${pct * 100}%`;
        if (bttRef.current) bttRef.current.classList.toggle('show', pct > 0.15);
        if (tgRef.current) tgRef.current.classList.toggle('show', pct > 0.3);
        scrollRaf.current = null;
      });
    };
    window.addEventListener('scroll', h, { passive: true });
    h();
    return () => { window.removeEventListener('scroll', h); cancelAnimationFrame(scrollRaf.current); };
  }, []);

  useEffect(() => {
    const h = e => {
      if (cursorRef.current) cursorRef.current.style.transform = `translate(${e.clientX - 4}px, ${e.clientY - 4}px)`;
      const mx = e.clientX / window.innerWidth - 0.5;
      const my = e.clientY / window.innerHeight - 0.5;
      if (heroContentRef.current) heroContentRef.current.style.transform = `translate(${mx * -12}px, ${my * -8}px)`;
      if (heroGhostRef.current) heroGhostRef.current.style.transform = `translate(calc(-50% + ${mx * -30}px), calc(-50% + ${my * -20}px))`;
    };
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(en => en.forEach(e => { if (e.isIntersecting) { if (!visRef.current.has(e.target.id)) { visRef.current.add(e.target.id); setVis(new Set(visRef.current)); if (e.target.id === 'gallery' && galRef.current) { galRef.current.scrollTo({ left: 60, behavior: 'smooth' }); setTimeout(() => galRef.current?.scrollTo({ left: 0, behavior: 'smooth' }), 800); } } if (navLinksRef.current) navLinksRef.current.querySelectorAll('.N-a').forEach(btn => btn.classList.toggle('on', btn.dataset.id === e.target.id)); } }), { threshold: 0.12, rootMargin: '-50px' });
    document.querySelectorAll('section[id]').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    const h = e => {
      if (e.key === 'Escape') { setVideoModal(null); setMenuOpen(false); setLightbox(null); }
      if (e.key === 'ArrowRight') setLightbox(lb => { if (!lb) return null; const imgs = GALLERY_ITEMS.filter(g => g.img && !g.video); const idx = imgs.indexOf(lb); return idx < imgs.length - 1 ? imgs[idx + 1] : imgs[0]; });
      if (e.key === 'ArrowLeft') setLightbox(lb => { if (!lb) return null; const imgs = GALLERY_ITEMS.filter(g => g.img && !g.video); const idx = imgs.indexOf(lb); return idx > 0 ? imgs[idx - 1] : imgs[imgs.length - 1]; });
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  useEffect(() => {
    document.body.style.overflow = (videoModal || lightbox || menuOpen) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [videoModal, lightbox, menuOpen]);

  const go = id => { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' }); setMenuOpen(false); };
  const galRef = useRef(null);
  const galDrag = useRef({ active: false, moved: false, startX: 0, scrollLeft: 0 });
  const onGalDown = e => { galDrag.current = { active: true, moved: false, startX: e.pageX - galRef.current.offsetLeft, scrollLeft: galRef.current.scrollLeft }; galRef.current.style.cursor = 'grabbing'; };
  const onGalUp = () => { galDrag.current.active = false; if (galRef.current) galRef.current.style.cursor = 'grab'; };
  const onGalMove = e => { if (!galDrag.current.active) return; galDrag.current.moved = true; e.preventDefault(); const x = e.pageX - galRef.current.offsetLeft; galRef.current.scrollLeft = galDrag.current.scrollLeft - (x - galDrag.current.startX) * 1.5; };
  const onGalClick = (g) => { if (galDrag.current.moved) return; if (g.video) setVideoModal(g.video); else if (g.img) setLightbox(g); };

  const playTrack = i => {
    if (playing === i) {
      if (audioRef.current) {
        if (paused) { audioRef.current.play(); setPaused(false); }
        else { audioRef.current.pause(); setPaused(true); }
      }
      return;
    }
    const track = TRACKS[i];
    if (!track.url) {
      window.open(LINKS.spotify, '_blank');
      return;
    }
    setPlaying(i);
    setPaused(false);
    if (fpProgRef.current) fpProgRef.current.style.width = '0%';
    if (audioRef.current) {
      audioRef.current.src = track.url;
      audioRef.current.play();
    }
  };

  const stopTrack = () => {
    setPlaying(null);
    setPaused(false);
    if (fpProgRef.current) fpProgRef.current.style.width = '0%';
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
  };
  const [sending, setSending] = useState(false);
  const onSubmit = async e => {
    e.preventDefault();
    setSending(true);
    const fd = new FormData(e.target);
    fd.append('_subject', `Новая заявка от ${fd.get('name')}`);
    fd.append('_template', 'table');
    try {
      await fetch('https://formsubmit.co/ajax/booking@juliamilows.com', {
        method: 'POST',
        body: fd,
      });
      setSubmitted(true);
      e.target.reset();
      setTimeout(() => setSubmitted(false), 5000);
    } catch {
      alert(lang === 'ru' ? 'Ошибка отправки. Попробуйте позже.' : 'Send error. Please try again.');
    }
    setSending(false);
  };
  const CH = () => { if (cursorRef.current) cursorRef.current.classList.add('hover'); };
  const CL = () => { if (cursorRef.current) cursorRef.current.classList.remove('hover'); };

  return (
    <div className="app-root" onMouseEnter={CL}>
      <a href="#about" className="skip-link">Skip to content</a>

      {curtain && (<div className={`curtain ${curtainOpen ? 'open' : ''}`}><div className="curtain-logo">JULIA MILOWS</div><div className="curtain-l" /><div className="curtain-r" /></div>)}

      <div ref={cursorRef} className="cursor"><div className="cursor-dot" /><div className="cursor-ring" /></div>
      <div ref={scrollProgRef} className="scroll-prog" />
      <div className="noise" />

      {/* NAV */}
      <nav className="N" ref={navRef}>
        <div className="N-i">
          <div className="N-logo" onClick={() => go('hero')} onMouseEnter={CH} onMouseLeave={CL}>Julia Milows</div>
          <ul className="N-links" ref={navLinksRef}>{SECTION_IDS.map((id, i) => <li key={id}><button className="N-a" data-id={id} onClick={() => go(id)} onMouseEnter={CH} onMouseLeave={CL}>{t.nav[i]}</button></li>)}</ul>
          <div className="N-r">
            <button className="N-lang" onClick={() => setLang(nextLang())} onMouseEnter={CH} onMouseLeave={CL}>{nextLang().toUpperCase()}</button>
            <button className="N-ham" onClick={() => setMenuOpen(true)} aria-label="Menu"><span /><span /><span /></button>
          </div>
        </div>
      </nav>

      <div className={`MM ${menuOpen ? 'open' : ''}`}>
        <button style={{ position: 'absolute', top: 20, right: 28, fontSize: 28, color: 'var(--td)', letterSpacing: 0 }} onClick={() => setMenuOpen(false)} aria-label="Close menu">✕</button>
        {SECTION_IDS.map((id, i) => <button key={id} onClick={() => go(id)}>{t.nav[i]}</button>)}
        <button onClick={() => { setLang(nextLang()); setMenuOpen(false); }} style={{ color: 'var(--g)', fontSize: 22 }}>{({ ru: 'English', en: 'العربية', ar: 'Русский' })[lang]}</button>
      </div>

      {/* HERO */}
      <section id="hero" className="H" ref={heroRef}>
        <div className="H-bg" />
        <div className="H-canvas"><Suspense><WireSphere /></Suspense></div>
        <div className="H-gradient" /><div className="H-vignette" />
        <div className="H-ghost" ref={heroGhostRef}>MILOWS</div>
        <div className="H-line H-line-l" />
        <div className="H-line H-line-r" />
        <div className="H-corner H-corner-tl" />
        <div className="H-corner H-corner-br" />
        <div className="H-content" ref={heroContentRef}>
          <div className="H-julia">Julia</div>
          <div className="H-milows">Milows</div>
          <p className="H-sub">{t.hero.sub}</p>
          <div className="H-btns">
            <MagBtn className="B1" onClick={() => go('booking')} onMouseEnter={CH} onMouseLeave={CL}>{t.hero.cta}</MagBtn>
            <MagBtn className="B2" onClick={() => go('music')} onMouseEnter={CH} onMouseLeave={CL}>{t.hero.cta2}</MagBtn>
          </div>
        </div>
        <div className="H-badge H-badge-1" style={{ top: '18%', right: '12%' }}>MOSCOW</div>
        <div className="H-badge H-badge-2" style={{ bottom: '22%', left: '8%' }}>EST. 2020</div>
        <div className="H-badge H-badge-3" style={{ top: '65%', right: '8%' }}>POP · R'N'B · EDM</div>
        <div className="H-scroll"><div className="H-scroll-text">Scroll</div><div className="H-scroll-line" /></div>
      </section>

      <div className="MQ"><div className="MQ-inner"><span className="MQ-text">{t.marquee.repeat(4)}</span></div></div>

      {/* ABOUT */}
      <section id="about" className={`S ${vis.has('about') ? 'v' : ''}`}>
        <div className="S-tag">{t.about.tag}</div>
        <h2 className="S-title">{t.about.title} <span className="ac">{t.about.accent}</span></h2>
        <div className="A-grid">
          <div className="A-text"><p>{t.about.p1}</p><p>{t.about.p2}</p><p>{t.about.p3}</p></div>
          <div className="A-photo"><img src="/images/about-portrait.webp" alt="Julia Milows" className="A-photo-img" loading="lazy" /><div className="A-photo-bg" /></div>
        </div>
        <div className="A-stats">{STATS.map((s, i) => <div key={i} className="A-stat" onMouseEnter={CH} onMouseLeave={CL}><div className="A-stat-n"><Counter target={s.n} suffix={s.s} visible={vis.has('about')} /></div><div className="A-stat-l">{s.l[li]}</div></div>)}</div>
      </section><div className="divider" />

      {/* MUSIC */}
      <section id="music" className={`S ${vis.has('music') ? 'v' : ''}`}>
        <div className="S-tag">{t.music.tag}</div>
        <h2 className="S-title">{t.music.title} <span className="ac">{t.music.accent}</span></h2>
        <div className="M-top"><div><Suspense><CircleViz playing={playing !== null} size={140} /></Suspense></div><div style={{ flex: 1 }}><p style={{ font: '400 14px/1.8 var(--sans)', color: 'var(--td)', maxWidth: 400 }}>{t.music.desc}</p></div></div>
        <div className="TL">{TRACKS.map((tr, i) => <div key={i} className={`TI ${playing === i ? 'on' : ''}`} onClick={() => playTrack(i)} onMouseEnter={CH} onMouseLeave={CL}><div className="TI-n">{playing === i && !paused ? '▶' : String(i + 1).padStart(2, '0')}</div><div><div className="TI-t">{tr.t}</div>{tr.f && <div className="TI-f">{tr.f}</div>}</div><div className="TI-y">{tr.y}</div><div className="TI-d">{tr.d}</div></div>)}</div>
        <div className="M-links">
          <a href={LINKS.spotify} target="_blank" rel="noopener noreferrer" className="B2" style={{ textDecoration: 'none', fontSize: 9, padding: '16px 32px' }} onMouseEnter={CH} onMouseLeave={CL}>{t.music.spot}</a>
          <a href={LINKS.appleMusic} target="_blank" rel="noopener noreferrer" className="B2" style={{ textDecoration: 'none', fontSize: 9, padding: '16px 32px' }} onMouseEnter={CH} onMouseLeave={CL}>{t.music.apple}</a>
        </div>
        <div className="M-embed">
          {vis.has('music') && <iframe src="https://open.spotify.com/embed/artist/2JfnSnLG9WTRq77gICScuP?utm_source=generator&theme=0" width="100%" height="152" frameBorder="0" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" title="Spotify" />}
        </div>
      </section><div className="divider" />

      {/* SERVICES */}
      <section id="services" className={`S ${vis.has('services') ? 'v' : ''}`}>
        <div className="S-tag">{t.services.tag}</div>
        <h2 className="S-title">{t.services.title} <span className="ac">{t.services.accent}</span></h2>
        <div className="SV-grid">{t.services.items.map((s, i) => <TiltCard key={i} className="SV-card"><div className="SV-icon">{['♪','♫','✎','♬','🎤','🎓'][i]}</div><div className="SV-num">{s.i}</div><div className="SV-name">{s.t}</div><div className="SV-desc">{s.d}</div><div className="SV-price">{s.p}</div></TiltCard>)}</div>
      </section><div className="divider" />

      {/* EPK */}
      <section id="epk" className={`S ${vis.has('epk') ? 'v' : ''}`}>
        <div className="S-tag">{t.epk.tag}</div>
        <h2 className="S-title">{t.epk.title} <span className="ac">{t.epk.accent}</span></h2>
        <div className="S-sub">{t.epk.sub}</div>
        <div className="EPK-layout">
          <div className="EPK-grid">
            <div className="EPK-b"><div className="EPK-bt">{t.epk.bioLabel}</div><p>{t.epk.bio}</p></div>
            <div className="EPK-b"><div className="EPK-bt">{t.epk.achLabel}</div><ul>{t.epk.ach.map((a, i) => <li key={i}>{a}</li>)}</ul></div>
            <div className="EPK-b EPK-full"><div className="EPK-bt">{t.epk.riderLabel}</div><p>{t.epk.rider}</p></div>
          </div>
          <div className="EPK-photo"><img src="/images/epk-portrait.webp" alt="Julia Milows" loading="lazy" /></div>
        </div>
        <div style={{ marginTop: 48 }}><a href="/press-kit.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}><MagBtn className="B1" onMouseEnter={CH} onMouseLeave={CL}>{t.epk.dl}</MagBtn></a></div>
      </section><div className="divider" />

      {/* GALLERY */}
      <section id="gallery" className={`S ${vis.has('gallery') ? 'v' : ''}`}>
        <div className="S-tag">{t.gallery.tag}</div>
        <h2 className="S-title">{t.gallery.title} <span className="ac">{t.gallery.accent}</span></h2>
        <div style={{ height: 48 }} />
        <div className="G-wrap" ref={galRef} style={{ cursor: 'grab' }} onMouseDown={onGalDown} onMouseUp={onGalUp} onMouseLeave={onGalUp} onMouseMove={onGalMove} onScroll={e => { const el = e.currentTarget; if (galProgRef.current) galProgRef.current.style.width = `${Math.max(el.scrollLeft / (el.scrollWidth - el.clientWidth) * 100, 5)}%`; }}><div className="G-row">{GALLERY_ITEMS.map((g, i) => <div key={i} className="G-item" onMouseEnter={CH} onMouseLeave={CL} onClick={() => onGalClick(g)}>{g.img ? <img src={g.img} alt={g.l} className="G-bg G-bg-img" loading="lazy" /> : <div className="G-bg" style={{ background: g.g }} />}{(g.c === 'VIDEO' || g.c === 'MUSIC VIDEO') && <div className="G-play">▶</div>}<div className="G-ov"><div className="G-c">{g.c}</div><div className="G-l">{g.l}</div></div></div>)}</div></div>
        <div className="G-prog-wrap"><div ref={galProgRef} className="G-prog-bar" style={{ width: '5%' }} /></div>
        <div className="G-hint">{t.gallery.scroll}</div>
      </section><div className="divider" />

      {/* BOOKING */}
      <section id="booking" className={`S ${vis.has('booking') ? 'v' : ''}`}>
        <div className="S-tag">{t.booking.tag}</div>
        <h2 className="S-title">{t.booking.title} <span className="ac">{t.booking.accent}</span></h2>
        <div className="S-sub">{t.booking.sub}</div>
        <div className="BK-layout">
          <form className="BK-form" onSubmit={onSubmit}>
            <input type="hidden" name="_captcha" value="false" />
            <div className="BK-row"><div className="BK-fg"><label className="BK-label" htmlFor="bk-name">{t.booking.f.name}</label><input className="BK-input" id="bk-name" name="name" required /></div><div className="BK-fg"><label className="BK-label" htmlFor="bk-email">{t.booking.f.email}</label><input className="BK-input" id="bk-email" name="email" type="email" required /></div></div>
            <div className="BK-row"><div className="BK-fg"><label className="BK-label" htmlFor="bk-phone">{t.booking.f.phone}</label><input className="BK-input" id="bk-phone" name="phone" type="tel" /></div><div className="BK-fg"><label className="BK-label" htmlFor="bk-city">{t.booking.f.city}</label><input className="BK-input" id="bk-city" name="city" /></div></div>
            <div className="BK-row"><div className="BK-fg"><label className="BK-label" htmlFor="bk-type">{t.booking.f.type}</label><select className="BK-sel" id="bk-type" name="event_type" required><option value="">—</option>{t.booking.types.map((et, i) => <option key={i}>{et}</option>)}</select></div><div className="BK-fg"><label className="BK-label" htmlFor="bk-date">{t.booking.f.date}</label><input className="BK-input" id="bk-date" name="date" type="date" min={TODAY} /></div></div>
            <div className="BK-fg"><label className="BK-label" htmlFor="bk-msg">{t.booking.f.msg}</label><textarea className="BK-ta" id="bk-msg" name="message" /></div>
            {submitted ? <div className="BK-ok">{t.booking.f.ok}</div> : <MagBtn className="B1" type="submit" disabled={sending} style={{ alignSelf: 'flex-start', opacity: sending ? 0.5 : 1 }} onMouseEnter={CH} onMouseLeave={CL}>{sending ? '...' : t.booking.f.send}</MagBtn>}
          </form>
          <div className="BK-info">
            <div><div className="BK-ci-l">Instagram</div><div className="BK-ci-v"><a href={LINKS.instagram} target="_blank" rel="noopener noreferrer" onMouseEnter={CH} onMouseLeave={CL}>@juliamilows</a></div></div>
            <div><div className="BK-ci-l">Telegram</div><div className="BK-ci-v"><a href={LINKS.telegram} target="_blank" rel="noopener noreferrer" onMouseEnter={CH} onMouseLeave={CL}>@juliamilows</a></div></div>
            <div><div className="BK-ci-l">Email</div><div className="BK-ci-v"><a href={`mailto:${LINKS.email}`} onMouseEnter={CH} onMouseLeave={CL}>{LINKS.email}</a></div></div>
            <div><div className="BK-ci-l">{t.booking.geoLabel}</div><div className="BK-ci-v">{t.booking.geo}</div></div>
            <div><div className="BK-ci-l" style={{ marginBottom: 12 }}>{t.booking.socialsLabel}</div><div className="social-row">{SOCIALS.map((s, i) => <a key={i} href={s.u} target="_blank" rel="noopener noreferrer" className="social-a" onMouseEnter={CH} onMouseLeave={CL}>{s.n}</a>)}</div></div>
          </div>
        </div>
      </section>

      {/* TELEGRAM */}
      <section className={`S ${vis.has('booking') ? 'v' : ''}`}>
        <div className="S-tag">{lang === 'ru' ? '07 / TELEGRAM' : lang === 'ar' ? '07 / تيليجرام' : '07 / TELEGRAM'}</div>
        <h2 className="S-title">{lang === 'ru' ? 'Следите за' : lang === 'ar' ? 'تابع' : 'Follow'} <span className="ac">{lang === 'ru' ? 'Новостями' : lang === 'ar' ? 'الأخبار' : 'the News'}</span></h2>
        <div className="TG-embed">
          <script async src="https://telegram.org/js/telegram-widget.js?22" data-telegram-post="juliamilowss/1" data-width="100%" data-dark="1" data-color="C4944A" data-dark-color="C4944A"></script>
          <div className="TG-cta">
            <a href="https://t.me/juliamilowss" target="_blank" rel="noopener noreferrer" className="B1" style={{ textDecoration: 'none', display: 'inline-block', fontSize: 9, padding: '16px 32px' }} onMouseEnter={CH} onMouseLeave={CL}>{lang === 'ru' ? 'Подписаться на канал' : lang === 'ar' ? 'اشترك في القناة' : 'Subscribe to Channel'}</a>
          </div>
        </div>
      </section>

      <div className="MQ"><div className="MQ-inner" style={{ animationDirection: 'reverse' }}><span className="MQ-text">{t.marquee.repeat(4)}</span></div></div>
      <footer className="F">
        <div className="F-top">
          <div className="F-brand">JULIA MILOWS</div>
          <div className="F-socials">{SOCIALS.map((s, i) => <a key={i} href={s.u} target="_blank" rel="noopener noreferrer" className="F-soc" onMouseEnter={CH} onMouseLeave={CL}>{s.n}</a>)}</div>
        </div>
        <div className="F-divider" />
        <div className="F-bottom">
          <p className="F-t">© {new Date().getFullYear()} JULIA MILOWS — {t.footer}</p>
          <a href={`mailto:${LINKS.email}`} className="F-email" onMouseEnter={CH} onMouseLeave={CL}>{LINKS.email}</a>
        </div>
      </footer>

      <audio
        ref={audioRef}
        preload="none"
        onTimeUpdate={() => { if (audioRef.current && fpProgRef.current) fpProgRef.current.style.width = (audioRef.current.currentTime / (audioRef.current.duration || 1) * 100) + '%'; }}
        onEnded={() => { const next = playing + 1; if (next < TRACKS.length && TRACKS[next].url) { playTrack(next); } else { stopTrack(); } }}
      />

      <div className={`FP ${playing !== null ? 'show' : ''}`}>
        <button className="FP-btn" onClick={() => playing !== null && playTrack(playing)} onMouseEnter={CH} onMouseLeave={CL} aria-label={paused ? 'Play' : 'Pause'}>{paused ? '▶' : '❚❚'}</button>
        {playing !== null && <div className="FP-info"><div className="FP-t">{TRACKS[playing]?.t}</div><div className="FP-f">{TRACKS[playing]?.f || 'Julia Milows'}</div></div>}
        <div className="FP-bar" onClick={e => { if (audioRef.current?.duration) { const r = e.currentTarget.getBoundingClientRect(); audioRef.current.currentTime = ((e.clientX - r.left) / r.width) * audioRef.current.duration; } }}><div ref={fpProgRef} className="FP-prog" style={{ width: '0%', animation: 'none' }} /></div>
        <button className="FP-x" onClick={stopTrack} onMouseEnter={CH} onMouseLeave={CL} aria-label="Close player">✕</button>
      </div>

      {/* BACK TO TOP */}
      <button ref={bttRef} className="btt" onClick={() => go('hero')} onMouseEnter={CH} onMouseLeave={CL} aria-label="Back to top">↑</button>

      {/* TELEGRAM QUICK CONTACT */}
      <a ref={tgRef} href="https://t.me/juliamilows" target="_blank" rel="noopener noreferrer" className="tg-float" onMouseEnter={CH} onMouseLeave={CL}>
        <svg viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 1 0 24 12.056A12.01 12.01 0 0 0 11.944 0Zm4.962 7.166c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472c-.18 1.898-.962 6.502-1.36 8.627c-.168.9-.499 1.201-.82 1.23c-.696.065-1.225-.46-1.9-.902c-1.056-.693-1.653-1.124-2.678-1.8c-1.185-.78-.417-1.21.258-1.91c.177-.184 3.247-2.977 3.307-3.23c.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345c-.48.33-.913.49-1.302.48c-.428-.008-1.252-.241-1.865-.44c-.752-.245-1.349-.374-1.297-.789c.027-.216.325-.437.893-.663c3.498-1.524 5.83-2.529 6.998-3.014c3.332-1.386 4.025-1.627 4.476-1.635Z"/></svg>
        <span>{lang === 'ru' ? 'Написать' : 'Message'}</span>
      </a>

      {/* VIDEO MODAL */}
      {videoModal && (
        <div className="VM" onClick={() => setVideoModal(null)}>
          <div className="VM-inner" onClick={e => e.stopPropagation()}>
            <button className="VM-close" onClick={() => setVideoModal(null)}>✕</button>
            <iframe src={`${videoModal}?autoplay=1`} width="100%" height="100%" frameBorder="0" allow="autoplay; encrypted-media; fullscreen" allowFullScreen title="Video" />
          </div>
        </div>
      )}

      {/* LIGHTBOX */}
      {lightbox && (() => {
        const imgs = GALLERY_ITEMS.filter(g => g.img && !g.video);
        const idx = imgs.indexOf(lightbox);
        const prev = () => setLightbox(idx > 0 ? imgs[idx - 1] : imgs[imgs.length - 1]);
        const next = () => setLightbox(idx < imgs.length - 1 ? imgs[idx + 1] : imgs[0]);
        return (
          <div className="LB" onClick={() => setLightbox(null)}>
            <button className="LB-close" onClick={() => setLightbox(null)} aria-label="Close">✕</button>
            <button className="LB-arrow LB-prev" onClick={e => { e.stopPropagation(); prev(); }} aria-label="Previous">‹</button>
            <img src={lightbox.img} alt={lightbox.l} className="LB-img" onClick={e => e.stopPropagation()} />
            <button className="LB-arrow LB-next" onClick={e => { e.stopPropagation(); next(); }} aria-label="Next">›</button>
            <div className="LB-cap"><span className="LB-cat">{lightbox.c}</span><span className="LB-label">{lightbox.l}</span><span className="LB-count">{idx + 1} / {imgs.length}</span></div>
          </div>
        );
      })()}

      {/* COOKIE CONSENT */}
      {!cookieOk && (
        <div className="CC">
          <p className="CC-text">{lang === 'ru' ? 'Этот сайт использует cookies для улучшения опыта.' : 'This site uses cookies to improve your experience.'}</p>
          <div className="CC-btns">
            <button className="CC-ok" onClick={() => { localStorage.setItem('cookie-consent', '1'); setCookieOk(true); }}>{lang === 'ru' ? 'Принять' : 'Accept'}</button>
            <button className="CC-no" onClick={() => setCookieOk(true)}>{lang === 'ru' ? 'Отклонить' : 'Decline'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
